/**
 * ProposalCard - an assistant proposal, confirmed or cancelled by the person
 * (server/lib/chatActions.js builds it; nothing has changed while it shows).
 *
 * Every action lists each change in plain words, before -> after, with a
 * checkbox (all ticked to start), its reason and the server's notes (e.g.
 * "same quote #: saved as another line"), and a link to open the record.
 * Confirm sends the ticked ones, in order, through proposalActions.runAction
 * (the app's own endpoints, the person's own token) and shows each result:
 * done with a link, or the server's reason. A refusal that is a question gets
 * the editor's answers:
 *   quote_taken    use the next free quote # / keep it (another line)
 *   edit_conflict  save mine anyway / open the record in the editor
 * A record that became Won opens the Won project setup (the PO form), as
 * every other route to Won does. Cancel sends nothing.
 *
 * Controlled: `state` ({ phase, selected, results, won }) lives in ChatWidget
 * so closing the panel never forgets what already ran (no double Confirm).
 * onOutcome(text) tells the model what happened (an <app_outcome> message).
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import WonProjectSetup from '../opportunities/WonProjectSetup';
import { runAction, outcomeText, resultLine } from './proposalActions';

const GREEN = '#1B5E20';
const RED = '#B3261E';

/** A proposal's starting state: nothing run, every action ticked. */
export const initialProposalState = (proposal) => ({
  phase: 'pending',
  selected: Object.fromEntries((proposal.actions || []).map((a) => [a.key, true])),
  results: {},
  won: null,
});

const btn = (primary, disabled) => ({
  background: disabled ? '#C9D3C7' : primary ? GREEN : '#fff',
  color: primary ? '#fff' : GREEN,
  border: `1px solid ${disabled ? '#C9D3C7' : GREEN}`,
  borderRadius: 7,
  padding: '5px 11px',
  fontSize: 11.5,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: disabled ? 'default' : 'pointer',
});
const linkStyle = { color: GREEN, fontWeight: 700, fontSize: 11, textDecoration: 'underline', cursor: 'pointer' };
const blank = (v) => (v === null || v === undefined || v === '' ? '(blank)' : v);

function ChangeRows({ changes }) {
  if (!changes || !changes.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
      {changes.map((c) => (
        <div key={c.field} data-testid={`change-${c.field}`} style={{ fontSize: 11.5, lineHeight: 1.45 }}>
          <span style={{ fontWeight: 700 }}>{c.label}:</span>{' '}
          {c.before !== null && c.before !== undefined && (
            <>
              <span style={{ color: '#888', textDecoration: 'line-through' }}>{c.before}</span>
              {' → '}
            </>
          )}
          {(c.before === null || c.before === undefined) && <span style={{ color: '#888' }}>{'(blank) → '}</span>}
          <span style={{ fontWeight: 600 }}>{blank(c.after)}</span>
        </div>
      ))}
    </div>
  );
}

function Copied({ copied }) {
  const [open, setOpen] = useState(false);
  if (!copied || !copied.length) return null;
  return (
    <div style={{ marginTop: 4, fontSize: 11 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, fontFamily: 'inherit' }}
      >
        {open ? 'Hide' : 'Show'} the {copied.length} fields copied from the source
      </button>
      {open && (
        <ul style={{ margin: '3px 0 0', paddingLeft: 16 }}>
          {copied.map((c) => (
            <li key={c.field}>
              <b>{c.label}:</b> {blank(c.value)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Result({ action, result, busy, onRetry }) {
  if (!result) return null;
  const go = (href) => {
    if (href) window.location.hash = href;
  };
  if (result.ok) {
    return (
      <div role="status" style={{ marginTop: 5, fontSize: 11.5, color: GREEN, fontWeight: 700 }}>
        ✓ Done{' '}
        {result.href && (
          <a
            href={result.href}
            onClick={(e) => {
              e.preventDefault();
              go(result.href);
            }}
            style={linkStyle}
          >
            Open the record
          </a>
        )}
      </div>
    );
  }
  const body = result.body || {};
  return (
    <div role="alert" style={{ marginTop: 5, fontSize: 11.5, color: RED, fontWeight: 600 }}>
      ✗ {result.error}
      {result.code === 'quote_taken' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
          {body.next && (
            <button
              type="button"
              disabled={busy}
              style={btn(true, busy)}
              onClick={() => onRetry(action, { quote: body.next, allowDuplicateQuote: false })}
            >
              Use next free quote # {body.next}
            </button>
          )}
          <button type="button" disabled={busy} style={btn(false, busy)} onClick={() => onRetry(action, { allowDuplicateQuote: true })}>
            Keep quote # {body.quote} (another line)
          </button>
        </div>
      )}
      {result.code === 'edit_conflict' && (
        <div style={{ marginTop: 4, color: '#444', fontWeight: 500 }}>
          {(body.conflicts || []).map((c) => (
            <div key={c.field}>
              {c.field}: now {blank(c.theirs)}
              {body.changed_by && body.changed_by.actor ? ` (changed by ${body.changed_by.actor})` : ''}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
            <button type="button" disabled={busy} style={btn(true, busy)} onClick={() => onRetry(action, { force: true })}>
              Save mine anyway
            </button>
            {action.target && action.target.href && (
              <button type="button" style={btn(false, false)} onClick={() => go(action.target.href)}>
                Open in editor
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function ProposalCard({ proposal, state, onState, api, onOutcome, onSavePo }) {
  const s = state || initialProposalState(proposal);
  const [busy, setBusy] = useState(false);
  const pending = s.phase === 'pending';
  const picked = proposal.actions.filter((a) => s.selected[a.key]);

  const confirm = async () => {
    if (!pending || busy || !picked.length) return;
    setBusy(true);
    onState((cur) => ({ ...cur, phase: 'running' }));
    const results = {};
    let won = null;
    for (const a of picked) {
      // one at a time, in the order shown: a later action may need an earlier one
      // eslint-disable-next-line no-await-in-loop
      const r = await runAction(a, api);
      results[a.key] = r;
      if (r.ok && a.exec && a.exec.wonPrompt && r.body && !won) won = r.body;
      onState((cur) => ({ ...cur, results: { ...cur.results, [a.key]: r } }));
    }
    onState((cur) => ({ ...cur, phase: 'done', won }));
    setBusy(false);
    if (onOutcome) onOutcome(outcomeText(proposal, results));
  };

  const cancel = () => {
    if (!pending) return;
    onState((cur) => ({ ...cur, phase: 'cancelled' }));
    if (onOutcome) onOutcome(outcomeText(proposal, {}, { cancelled: true }));
  };

  const retry = async (action, opts) => {
    if (busy) return;
    setBusy(true);
    const r = await runAction(action, api, opts);
    onState((cur) => ({
      ...cur,
      results: { ...cur.results, [action.key]: r },
      won: r.ok && action.exec && action.exec.wonPrompt && r.body ? r.body : cur.won,
    }));
    setBusy(false);
    if (onOutcome) onOutcome(`Follow-up on proposal ${proposal.id}:\n- ${resultLine(action, r)}`);
  };

  const toggle = (key) => {
    if (pending) onState((cur) => ({ ...cur, selected: { ...cur.selected, [key]: !cur.selected[key] } }));
  };

  return (
    <div
      data-testid={`proposal-${proposal.id}`}
      style={{
        alignSelf: 'stretch',
        border: `1px solid ${pending ? GREEN : '#D8DED4'}`,
        borderRadius: 10,
        background: '#fff',
        padding: '10px 12px',
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0D0D0D' }}>
          {pending
            ? 'Proposed - nothing changed yet'
            : s.phase === 'running'
              ? 'Making the changes…'
              : s.phase === 'cancelled'
                ? 'Cancelled - nothing changed'
                : 'Confirmed'}
        </span>
        <span style={{ fontSize: 10.5, color: '#777' }}>
          {proposal.actions.length} change{proposal.actions.length === 1 ? '' : 's'}
        </span>
      </div>
      {proposal.summary && <div style={{ fontSize: 11.5, color: '#555', marginTop: 2 }}>{proposal.summary}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        {proposal.actions.map((a) => (
          <div key={a.key} style={{ borderTop: '1px solid #EEF0EC', paddingTop: 7, opacity: !pending && !s.results[a.key] ? 0.55 : 1 }}>
            <label style={{ display: 'flex', gap: 7, alignItems: 'flex-start', cursor: pending ? 'pointer' : 'default' }}>
              <input
                type="checkbox"
                checked={!!s.selected[a.key]}
                disabled={!pending}
                onChange={() => toggle(a.key)}
                aria-label={`Include: ${a.title}`}
                style={{ marginTop: 2, accentColor: GREEN }}
              />
              <span style={{ fontWeight: 700, color: '#0D0D0D' }}>{a.title}</span>
            </label>
            <div style={{ paddingLeft: 21 }}>
              {a.reason && <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>{a.reason}</div>}
              <ChangeRows changes={a.changes} />
              <Copied copied={a.copied} />
              {(a.notes || []).map((n) => (
                <div key={n} style={{ fontSize: 10.5, color: '#5B6B58', marginTop: 3 }}>
                  {n}
                </div>
              ))}
              {a.target && a.target.href && !s.results[a.key] && (
                <a
                  href={a.target.href}
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.hash = a.target.href;
                  }}
                  style={{ ...linkStyle, display: 'inline-block', marginTop: 3 }}
                >
                  Open in editor
                </a>
              )}
              <Result action={a} result={s.results[a.key]} busy={busy} onRetry={retry} />
            </div>
          </div>
        ))}
      </div>

      {proposal.rejected && proposal.rejected.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#7A4B00' }}>
          Not proposed: {proposal.rejected.map((r) => r.error).join(' · ')}
        </div>
      )}

      {pending && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button type="button" onClick={cancel} disabled={busy} style={btn(false, busy)}>
            Cancel
          </button>
          <button type="button" onClick={confirm} disabled={busy || !picked.length} style={btn(true, busy || !picked.length)}>
            Confirm{picked.length !== proposal.actions.length ? ` ${picked.length}` : ''}
          </button>
        </div>
      )}

      {s.won &&
        onSavePo &&
        createPortal(
          <div style={{ position: 'relative', zIndex: 1100 }}>
            <WonProjectSetup
              opp={s.won}
              onClose={() => onState((cur) => ({ ...cur, won: null }))}
              onSave={(patch) => onSavePo(s.won.id, patch)}
            />
          </div>,
          document.body
        )}
    </div>
  );
}

export default ProposalCard;
